import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
} from "react";
import { useArtist } from "./ArtistContext";
import { get } from "../services/api";

const ArtistDataContext = createContext();

export const useArtistData = () => {
  const context = useContext(ArtistDataContext);
  if (!context) {
    throw new Error("useArtistData must be used within an ArtistDataProvider");
  }
  return context;
};

export const ArtistDataProvider = ({ children }) => {
  const { selectedArtist } = useArtist();

  // Shows cache: { [artistId or 'all']: { shows: [], loading: boolean, error: string, hasFetched: boolean } }
  const [showsCache, setShowsCache] = useState({});

  // Earnings cache: { [cacheKey]: { earnings: [], totals: {}, loading: boolean, error: string, hasFetched: boolean } }
  // cacheKey format: `${artistId or 'all'}_${startDate}_${endDate}`
  const [earningsCache, setEarningsCache] = useState({});

  // Availability cache: { [artistId]: { weekly: [], blocks: [], loading: boolean, error: string, hasFetched: boolean } }
  const [availabilityCache, setAvailabilityCache] = useState({});

  // Profile cache: { [artistId]: { profile: {}, loading: boolean, error: string, hasFetched: boolean } }
  const [profileCache, setProfileCache] = useState({});

  // Track previous artist to detect changes
  const previousArtistIdRef = useRef(null);

  // Auto-fetch shows when artist selection changes (if not cached)
  useEffect(() => {
    const currentArtistId = selectedArtist?.artist_id || "all";
    const cacheKey = currentArtistId === "all" ? "all" : currentArtistId;
    
    // Only fetch if not already cached
    const cached = showsCache[cacheKey];
    if (!cached || !cached.hasFetched) {
      fetchShows(selectedArtist?.artist_id || null);
    }
    
    previousArtistIdRef.current = currentArtistId;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedArtist?.artist_id]);

  // Fetch Shows for an artist (or all artists)
  const fetchShows = useCallback(async (artistId = null) => {
    const cacheKey = artistId || "all";

    // Set loading state
    setShowsCache((prev) => ({
      ...prev,
      [cacheKey]: { ...prev[cacheKey], loading: true, error: null },
    }));

    try {
      const params = new URLSearchParams();
      if (artistId) {
        params.append("artistId", artistId);
      }

      const data = await get(`/shows/me?${params.toString()}`);

      if (data.success) {
        setShowsCache((prev) => ({
          ...prev,
          [cacheKey]: {
            shows: data.shows || [],
            loading: false,
            error: null,
            hasFetched: true,
          },
        }));
      } else {
        setShowsCache((prev) => ({
          ...prev,
          [cacheKey]: {
            shows: [],
            loading: false,
            error: data.error || "Failed to fetch shows",
            hasFetched: true,
          },
        }));
      }
    } catch (err) {
      console.error("Error fetching shows:", err);
      setShowsCache((prev) => ({
        ...prev,
        [cacheKey]: {
          shows: [],
          loading: false,
          error: err.message,
          hasFetched: true,
        },
      }));
    }
  }, []);

  // Get cached Shows data
  const getShows = useCallback(
    (artistId = null) => {
      const cacheKey = artistId || "all";
      const cached = showsCache[cacheKey];
      if (!cached) {
        return { shows: [], loading: false, error: null, hasFetched: false };
      }
      return { ...cached, hasFetched: cached.hasFetched !== false };
    },
    [showsCache]
  );

  // Fetch Earnings for an artist (or all artists) with date range
  const fetchEarnings = useCallback(async (artistId = null, startDate = null, endDate = null) => {
    const cacheKey = `${artistId || "all"}_${startDate || ""}_${endDate || ""}`;

    // Set loading state
    setEarningsCache((prev) => ({
      ...prev,
      [cacheKey]: { ...prev[cacheKey], loading: true, error: null },
    }));

    try {
      const params = new URLSearchParams();
      if (startDate) params.append("startDate", startDate);
      if (endDate) params.append("endDate", endDate);
      if (artistId) params.append("artistId", artistId);

      const data = await get(`/earnings/me?${params.toString()}`);

      if (data.success) {
        setEarningsCache((prev) => ({
          ...prev,
          [cacheKey]: {
            earnings: data.earnings || [],
            totals: data.totals || null,
            loading: false,
            error: null,
            hasFetched: true,
          },
        }));
      } else {
        setEarningsCache((prev) => ({
          ...prev,
          [cacheKey]: {
            earnings: [],
            totals: null,
            loading: false,
            error: data.error || "Failed to fetch earnings",
            hasFetched: true,
          },
        }));
      }
    } catch (err) {
      console.error("Error fetching earnings:", err);
      setEarningsCache((prev) => ({
        ...prev,
        [cacheKey]: {
          earnings: [],
          totals: null,
          loading: false,
          error: err.message,
          hasFetched: true,
        },
      }));
    }
  }, []);

  // Get cached Earnings data
  const getEarnings = useCallback(
    (artistId = null, startDate = null, endDate = null) => {
      const cacheKey = `${artistId || "all"}_${startDate || ""}_${endDate || ""}`;
      const cached = earningsCache[cacheKey];
      if (!cached) {
        return {
          earnings: [],
          totals: null,
          loading: false,
          error: null,
          hasFetched: false,
        };
      }
      return { ...cached, hasFetched: true };
    },
    [earningsCache]
  );

  // Invalidate specific cache
  const invalidateCache = useCallback((cacheType, artistId = null) => {
    switch (cacheType) {
      case "shows":
        if (artistId !== undefined) {
          const cacheKey = artistId || "all";
          setShowsCache((prev) => {
            const newCache = { ...prev };
            delete newCache[cacheKey];
            return newCache;
          });
        } else {
          setShowsCache({});
        }
        break;
      case "earnings":
        if (artistId !== undefined) {
          // Remove all earnings cache entries for this artist
          setEarningsCache((prev) => {
            const newCache = {};
            Object.keys(prev).forEach((key) => {
              const keyPrefix = artistId ? `${artistId}_` : "all_";
              if (!key.startsWith(keyPrefix)) {
                newCache[key] = prev[key];
              }
            });
            return newCache;
          });
        } else {
          setEarningsCache({});
        }
        break;
      case "availability":
        if (artistId !== undefined) {
          setAvailabilityCache((prev) => {
            const newCache = { ...prev };
            delete newCache[artistId];
            return newCache;
          });
        } else {
          setAvailabilityCache({});
        }
        break;
      case "profile":
        if (artistId !== undefined) {
          setProfileCache((prev) => {
            const newCache = { ...prev };
            delete newCache[artistId];
            return newCache;
          });
        } else {
          setProfileCache({});
        }
        break;
      default:
        console.warn(`Unknown cache type: ${cacheType}`);
    }
  }, []);

  // Fetch Availability for an artist
  const fetchAvailability = useCallback(async (artistId) => {
    if (!artistId) return;

    // Set loading state
    setAvailabilityCache((prev) => ({
      ...prev,
      [artistId]: { ...prev[artistId], loading: true, error: null },
    }));

    try {
      const [weeklyData, blocksData] = await Promise.all([
        get(`/availability/weekly?artistId=${artistId}`),
        get(`/availability/blocks?artistId=${artistId}`),
      ]);

      if (weeklyData.success && blocksData.success) {
        // Store the raw availability data from API - don't process it here
        // Processing will happen in the component to ensure correct format
        const availabilityData = (weeklyData.availability || []).map((avail) => ({
          ...avail,
          // Ensure day_of_week is a number
          day_of_week: typeof avail.day_of_week === 'string' 
            ? parseInt(avail.day_of_week, 10) 
            : avail.day_of_week,
          // Preserve exact times from database
          start_time: avail.start_time,
          end_time: avail.end_time,
        }));

        setAvailabilityCache((prev) => ({
          ...prev,
          [artistId]: {
            weekly: availabilityData,
            blocks: blocksData.blocks || [],
            loading: false,
            error: null,
            hasFetched: true,
          },
        }));
      } else {
        setAvailabilityCache((prev) => ({
          ...prev,
          [artistId]: {
            weekly: [],
            blocks: [],
            loading: false,
            error: weeklyData.error || blocksData.error || "Failed to fetch availability",
            hasFetched: true,
          },
        }));
      }
    } catch (err) {
      console.error("Error fetching availability:", err);
      setAvailabilityCache((prev) => ({
        ...prev,
        [artistId]: {
          weekly: [],
          blocks: [],
          loading: false,
          error: err.message,
          hasFetched: true,
        },
      }));
    }
  }, []);

  // Get cached Availability data
  const getAvailability = useCallback(
    (artistId) => {
      if (!artistId) {
        return { weekly: [], blocks: [], loading: false, error: null, hasFetched: false };
      }
      const cached = availabilityCache[artistId];
      if (!cached) {
        return { weekly: [], blocks: [], loading: false, error: null, hasFetched: false };
      }
      return { ...cached, hasFetched: true };
    },
    [availabilityCache]
  );

  // Update availability cache directly (for immediate updates after save)
  const updateAvailabilityCache = useCallback((artistId, updatedAvailability) => {
    if (!artistId || !updatedAvailability) return;
    
    setAvailabilityCache((prev) => {
      const current = prev[artistId] || { weekly: [], blocks: [] };
      let updatedWeekly = [...(current.weekly || [])];
      
      // Find and update existing entry
      const index = updatedWeekly.findIndex(
        (avail) => avail.day_of_week === updatedAvailability.day_of_week
      );
      
      if (index >= 0) {
        // Update existing entry
        updatedWeekly[index] = { ...updatedAvailability };
      } else {
        // Add new entry if it doesn't exist
        updatedWeekly.push({ ...updatedAvailability });
      }
      
      return {
        ...prev,
        [artistId]: {
          ...current,
          weekly: updatedWeekly,
          hasFetched: true,
          loading: false,
          error: null,
        },
      };
    });
  }, []);

  // Fetch Profile for an artist
  const fetchProfile = useCallback(async (artistId) => {
    if (!artistId) return;

    // Set loading state
    setProfileCache((prev) => ({
      ...prev,
      [artistId]: { ...prev[artistId], loading: true, error: null },
    }));

    try {
      const data = await get(`/artists/me?artistId=${artistId}`);

      if (data.success) {
        setProfileCache((prev) => ({
          ...prev,
          [artistId]: {
            profile: data.artist || {},
            loading: false,
            error: null,
            hasFetched: true,
          },
        }));
      } else {
        setProfileCache((prev) => ({
          ...prev,
          [artistId]: {
            profile: {},
            loading: false,
            error: data.error || "Failed to fetch profile",
            hasFetched: true,
          },
        }));
      }
    } catch (err) {
      console.error("Error fetching profile:", err);
      setProfileCache((prev) => ({
        ...prev,
        [artistId]: {
          profile: {},
          loading: false,
          error: err.message,
          hasFetched: true,
        },
      }));
    }
  }, []);

  // Get cached Profile data
  const getProfile = useCallback(
    (artistId) => {
      if (!artistId) {
        return { profile: {}, loading: false, error: null, hasFetched: false };
      }
      const cached = profileCache[artistId];
      if (!cached) {
        return { profile: {}, loading: false, error: null, hasFetched: false };
      }
      return { ...cached, hasFetched: true };
    },
    [profileCache]
  );

  // Clear all caches
  const clearCache = useCallback(() => {
    setShowsCache({});
    setEarningsCache({});
    setAvailabilityCache({});
    setProfileCache({});
  }, []);

  const value = {
    // Shows
    fetchShows,
    getShows,
    // Earnings
    fetchEarnings,
    getEarnings,
    // Availability
    fetchAvailability,
    getAvailability,
    updateAvailabilityCache,
    // Profile
    fetchProfile,
    getProfile,
    // Cache management
    invalidateCache,
    clearCache,
  };

  return (
    <ArtistDataContext.Provider value={value}>
      {children}
    </ArtistDataContext.Provider>
  );
};
